import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { COUNTRY_CONFIGS } from '@pakt/shared'

interface Props {
  value: string
  onChange: (code: string) => void
  showPrice?: boolean
}

export default function CountrySelector({ value, onChange, showPrice = false }: Props) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const countries = Object.values(COUNTRY_CONFIGS).filter(c =>
    c.nameEn.toLowerCase().includes(search.toLowerCase()) ||
    c.nameLocal.toLowerCase().includes(search.toLowerCase()) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  )

  const selected = COUNTRY_CONFIGS[value]

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <span className="flex items-center gap-2">
          {selected ? (
            <>
              <span className="text-xl">{selected.flag}</span>
              <span>{selected.nameLocal}</span>
              {showPrice && (
                <span className="text-sm text-gray-500">
                  ({selected.currency} {selected.signaturePriceLocal.toFixed(2)})
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-400">{t('landing.selectCountry', 'Select country')}</span>
          )}
        </span>
        <svg className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {countries.map(c => (
              <button
                key={c.code}
                type="button"
                onClick={() => { onChange(c.code); setOpen(false); setSearch('') }}
                className={`w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-indigo-50 ${value === c.code ? 'bg-indigo-100' : ''}`}
              >
                <span className="text-lg">{c.flag}</span>
                <span className="flex-1">{c.nameLocal}</span>
                {showPrice && (
                  <span className="text-sm text-gray-500">{c.currency} {c.signaturePriceLocal.toFixed(2)}</span>
                )}
                {c.launchStatus !== 'active' && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">{c.launchStatus}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
